import React, { Fragment } from 'react';
import { Transition, Dialog } from '@headlessui/react';
import Image from 'next/future/image';
import { CtaButton } from '@/components/Cta/Cta';
import reebeloCareIcon from '@/public/icons/product/reebeloCareIcon.svg';
import reebeloCareBanner from '@/public/images/banner/reebelocare-banner.png';
import WarrantyDisclaimer from './WarrantyDisclaimer';
import {
  generateWarrantyBenefitsList,
  generateWarrantySlideOverCta,
  generateWarrantySlideOverTitle,
} from '@/lib/reebelocare/helpers';

export interface WarrantySlideOverProps {
  isVisible: boolean;
  hideModal: () => void;
  warrantyPurchased: boolean;
  purchaseWarranty: () => void;
  price: number | string;
}

const BulletList = ({ text, icon }: { text: string; icon: any }) => (
  <div className="flex gap-3 text-sm">
    <div className="-mt-0.5 h-6 w-6 shrink-0">
      <Image
        alt="priority suport"
        src={icon}
        height={20}
        width={20}
        className="h-full w-full object-contain"
      />
    </div>

    <div
      className="flex items-center lg:items-start"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: text }}
    />
  </div>
);

export default function WarrantySlideOver(props: WarrantySlideOverProps) {
  return (
    <Transition.Root show={props.isVisible} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={props.hideModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-30"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-30"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-gray-700"
            onClick={props.hideModal}
          />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex w-full md:w-[550px]">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto relative w-full">
                  <div className="relative flex h-full flex-col bg-white">
                    <div className="px-4 py-4 shadow sm:px-6">
                      <div className="flex items-start justify-between">
                        <Dialog.Title
                          as="h3"
                          className="text-base font-bold leading-6 text-gray-700 xs:text-lg"
                        >
                          About ReebeloCare
                        </Dialog.Title>
                        <button
                          onClick={props.hideModal}
                          type="button"
                          className="btn-close opacity-100 md:h-3 md:w-3"
                          aria-label="Close"
                        ></button>
                      </div>
                    </div>
                    {/* Content  */}
                    <div className="relative overflow-y-auto">
                      <div className=" flex h-[150px] items-center justify-between overflow-y-hidden bg-[#ECFCF5]">
                        <div className="mt-2">
                          <Image
                            width={125}
                            height={125}
                            alt="close"
                            src={reebeloCareIcon}
                          />
                        </div>
                        <div className="h-full">
                          <Image
                            width={255}
                            height={260}
                            alt="rc"
                            src={reebeloCareBanner}
                          />
                        </div>
                      </div>
                      <div className="p-5">
                        <h4 className="text-center font-bold">
                          {generateWarrantySlideOverTitle()}
                        </h4>
                        <hr className="my-5" />
                        <div className="flex flex-col justify-center gap-5 lg:gap-6">
                          {generateWarrantyBenefitsList().map((item) => (
                            <BulletList
                              key={item.content}
                              text={item.content}
                              icon={item.icon}
                            />
                          ))}
                        </div>

                        <div className="mt-5 grid grid-cols-1">
                          <CtaButton
                            variant="dark"
                            className="block"
                            disabled={props.warrantyPurchased}
                            onClick={props.purchaseWarranty}
                          >
                            {props.warrantyPurchased
                              ? 'ReebeloCare Successfully Added'
                              : generateWarrantySlideOverCta(
                                  Number(props.price),
                                )}
                          </CtaButton>
                        </div>
                        <WarrantyDisclaimer />
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
